import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Badge, Button, ProgressBar, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import ResearchLayout from '../../components/ResearchLayout';

const DoctorBatchReport = () => {
  const { batchId, doctorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    loadDoctorBatchReport();
  }, [batchId, doctorId]);

  const loadDoctorBatchReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/batches/${batchId}/doctor/${doctorId}/report`);
      setReportData(response.data);
    } catch (err) {
      console.error('Error loading doctor batch report:', err);
      setError(err.response?.data?.error || 'Failed to load doctor batch report');
    } finally {
      setLoading(false);
    }
  };

  const openTextReport = async () => {
    try {
      const response = await api.get(`/batches/report/text/all`, {
        responseType: 'text'
      });
      
      // Open in new window with the text content
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write('<pre>' + response.data + '</pre>');
        newWindow.document.close();
      }
    } catch (err) {
      console.error('Error loading text report:', err);
      setError('Failed to load text report');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getProgressVariant = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Loading Doctor Batch Report">
        <div className="container-fluid">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading doctor batch report...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (error) {
    return (
      <ResearchLayout pageTitle="Doctor Batch Report Error">
        <div className="container-fluid">
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>Error Loading Report</Alert.Heading>
            <p>{error}</p>
            <div className="mt-3">
              <Button variant="outline-secondary" onClick={() => navigate(-1)} className="me-2">
                <i className="fas fa-arrow-left me-1"></i>
                Go Back
              </Button>
              <Button variant="primary" onClick={loadDoctorBatchReport}>
                <i className="fas fa-sync me-1"></i>
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      </ResearchLayout>
    );
  }

  if (!reportData) {
    return (
      <ResearchLayout pageTitle="No Data">
        <div className="container-fluid">
          <Alert variant="warning" className="mt-4">
            <Alert.Heading>No Report Data</Alert.Heading>
            <p>No report data available for this doctor and batch combination.</p>
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left me-1"></i>
              Go Back
            </Button>
          </Alert>
        </div>
      </ResearchLayout>
    );
  }

  const { batch, doctor, assignment, protocols, summary } = reportData;

  return (
    <ResearchLayout pageTitle={`Doctor Batch Report - ${doctor?.name}`}>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate(`/medical-admin/assign-batch-to-doctors/${batchId}`)}
              className="mb-2 me-2"
            >
              <i className="fas fa-arrow-left me-1"></i>
              Back to Batch
            </Button>
            <Button 
              variant="info" 
              onClick={openTextReport}
              className="mb-2"
            >
              <i className="fas fa-file-alt me-1"></i>
              View Batch Text Report
            </Button>
            <h2 className="mb-0">
              <i className="fas fa-chart-bar text-primary me-2"></i>
              Doctor Batch Report
            </h2>
            <p className="text-muted mb-0">
              Batch: <strong>{batch?.name}</strong> | Doctor: <strong>{doctor?.name}</strong>
            </p>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <Card.Title className="text-primary h3">{summary?.total_cases_in_batch || 0}</Card.Title>
                <Card.Text>Cases in Batch</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <Card.Title className="text-info h3">{summary?.total_protocols || 0}</Card.Title>
                <Card.Text>Protocols</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <Card.Title className="text-success h3">{summary?.completed_assignments || 0}</Card.Title>
                <Card.Text>Completed</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <Card.Title className="text-warning h3">{summary?.pending_assignments || 0}</Card.Title>
                <Card.Text>Pending</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Overall Progress */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="fas fa-tasks me-2"></i>
              Overall Progress
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={8}>
                <ProgressBar 
                  now={summary?.progress_percentage || 0} 
                  variant={getProgressVariant(summary?.progress_percentage || 0)}
                  label={`${summary?.progress_percentage || 0}%`}
                  className="mb-2"
                />
              </Col>
              <Col md={4} className="text-end">
                <Badge bg={getProgressVariant(summary?.progress_percentage || 0)} className="fs-6">
                  {summary?.completed_assignments || 0} of {summary?.total_assignments || 0} completed
                </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Batch and Doctor Info */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <i className="fas fa-layer-group me-2"></i>
                  Batch Information
                </h6>
              </Card.Header>
              <Card.Body>
                <Table bordered size="sm" className="mb-0">
                  <tbody>
                    <tr>
                      <td><strong>Batch Name</strong></td>
                      <td>{batch?.name}</td>
                    </tr>
                    <tr>
                      <td><strong>Status</strong></td>
                      <td>
                        <Badge bg={batch?.status === 'active' ? 'success' : 'secondary'}>
                          {batch?.status}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Start Date</strong></td>
                      <td>{formatDate(batch?.start_date)}</td>
                    </tr>
                    <tr>
                      <td><strong>End Date</strong></td>
                      <td>{formatDate(batch?.end_date)}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <i className="fas fa-user-md me-2"></i>
                  Doctor Information
                </h6>
              </Card.Header>
              <Card.Body>
                <Table bordered size="sm" className="mb-0">
                  <tbody>
                    <tr>
                      <td><strong>Doctor Name</strong></td>
                      <td>{doctor?.name}</td>
                    </tr>
                    <tr>
                      <td><strong>Email</strong></td>
                      <td>{doctor?.email}</td>
                    </tr>
                    <tr>
                      <td><strong>Role</strong></td>
                      <td>{doctor?.role}</td>
                    </tr>
                    <tr>
                      <td><strong>Assignment Status</strong></td>
                      <td>
                        <Badge bg={assignment?.status === 'active' ? 'success' : 'secondary'}>
                          {assignment?.status}
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Protocol Details */}
        {protocols?.map((protocol) => (
          <Card key={protocol.protocol_id} className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="fas fa-clipboard-list me-2"></i>
                  {protocol.protocol_name}
                </h6>
                <Badge bg="info" className="me-2">
                  {protocol.total_cases} Cases
                </Badge>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Protocol Progress */}
              <Row className="mb-3">
                <Col md={8}>
                  <ProgressBar 
                    now={protocol.progress_percentage || 0} 
                    variant={getProgressVariant(protocol.progress_percentage || 0)}
                    label={`${protocol.progress_percentage || 0}%`}
                  />
                </Col>
                <Col md={4} className="text-end">
                  <small className="text-muted">
                    {protocol.completed_assignments} / {protocol.total_assignments} completed
                  </small>
                </Col>
              </Row>

              {/* Protocol Stats */}
              <Row className="mb-3">
                <Col md={3}>
                  <small className="text-muted">Questions</small>
                  <div className="fw-bold">{protocol.total_questions}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Completed</small>
                  <div className="fw-bold text-success">{protocol.completed_assignments}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Pending</small>
                  <div className="fw-bold text-warning">{protocol.pending_assignments}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Progress</small>
                  <div className="fw-bold">{protocol.progress_percentage}%</div>
                </Col>
              </Row>

              {/* Cases Table */}
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Customer ID</th>
                    <th>Status</th>
                    <th>Questions Answered</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {protocol.cases?.map((caseItem) => (
                    <tr key={caseItem.case_id}>
                      <td>{caseItem.case_id}</td>
                      <td>{caseItem.customer_id}</td>
                      <td>
                        <Badge bg={caseItem.completed ? 'success' : 'warning'}>
                          {caseItem.completed ? 'Completed' : 'In Progress'}
                        </Badge>
                      </td>
                      <td>
                        {caseItem.questions_answered} / {caseItem.total_questions}
                      </td>
                      <td>
                        <ProgressBar 
                          now={caseItem.progress_percentage || 0}
                          variant={getProgressVariant(caseItem.progress_percentage || 0)}
                          className="mb-0"
                          style={{ height: '8px' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ))}

        {/* Navigation Buttons */}
        <div className="mt-4">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/medical-admin/assign-batch-to-doctors/${batchId}`)}
          >
            <i className="fas fa-arrow-left me-1"></i>
            Back to Batch Doctors Assignment
          </Button>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default DoctorBatchReport;
